import React, { useEffect, useMemo, useState } from 'react';
import SearchInput from 'component/atom/forms/input/searchInput/SearchInput';
import Button from 'component/atom/forms/button/Button';
import styles from './APISection.module.scss';
import APIRecords from './api-records/APIRecords';
import ConfirmationModal from 'component/hoc/success-modal/ConfirmationModal';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { usePost } from 'hooks/usePost';
import { Agent } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import Loader from 'component/atom/loader/Loader';
const APISection = ({
  selectedIds,
  setSelectedIds,
  testStatuses,
  setTestStatuses,
  bulkTaskMap,
  bulkRunNonce,
  setIsSyncingAll,
  isAgentConnected,
}) => {
  const { id } = useParams();

  const [didInitSelection, setDidInitSelection] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectAllTrigger, setSelectAllTrigger] = useState(false);
  const [accessType, setAccessType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [manualClear, setManualClear] = useState(false);
  const [bulkDoneCount, setBulkDoneCount] = useState(0);

  const { mutate: postConnections, isPending: isSubmitting } = usePost({
    onSuccessToast: 'Saved Successfully',
    toastType: 'add',
  });
  const {
    data: agentResponse,
    refetch,
    isPending,
    isLoading,
    isFetching,
  } = useFetch(['agent', id], Agent.getAgentAPISelection(id), {
    enable: true,
  });
  const agentData = agentResponse?.data;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  const handleDisconnect = () => {};
  const handleSelectAllToggle = () => {
    setSelectAllTrigger((prev) => !prev);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      pullFrequencies:
        agentResponse?.data?.agentConnections?.reduce((acc, item) => {
          acc[item.id] = item?.details?.detail?.pullFrequency ?? 0;
          return acc;
        }, {}) ?? {},
    },
    onSubmit: (values) => {
      const payload = {
        agentId: id,
        connectionDetails:
          agentResponse?.data?.agentConnections?.map((item) => ({
            agentConnectionId: item.id,
            pullFrequency: values.pullFrequencies[item.id],
            checked: selectedIds.includes(item.id),
          })) ?? [],
      };

      postConnections(
        {
          url: Agent.SaveConnectionDetails,
          data: payload,
          requireAuth: true,
        },
        {
          onSuccess: () => {
            setSelectAllTrigger(false);
            refetch();
          },
        }
      );
    },
  });

  const rightButtons = [
    {
      label: 'Select All',
      // opicBackground: true,
      func: handleSelectAllToggle,
      variant: 'light',
    },
    {
      label: 'Clear',
      // opicBackground: true,
      func: () => {
        setManualClear(true);
        setSelectAllTrigger(false);
        setSelectedIds([]);
      },
      variant: 'light',
    },
    {
      label: 'Save',
      svgType: 'save',
      func: formik.handleSubmit,
      isSubmitting: isSubmitting,
      variant: 'primary',
    },
  ];
  const filteredData = useMemo(() => {
    if (!agentData?.agentConnections) return [];

    const normalize = (s) => (s ?? '').toString().toLowerCase().trim();
    const search = normalize(searchTerm);

    return agentData.agentConnections.filter((item) => {
      const matchesAccess =
        accessType !== null && accessType !== -1
          ? item?.type === accessType
          : true;

      if (!matchesAccess) return false;
      if (!search) return true;

      const haystack = [
        item?.title,
        item?.description,
        item?.endpoint,
        item?.apiName,
        testStatuses[item.id] ?? 'Untested',
      ]
        .filter(Boolean)
        .map(normalize)
        .join(' ');

      return haystack.includes(search);
    });
  }, [agentData, accessType, searchTerm, testStatuses]);

  useEffect(() => {
    if (manualClear) {
      setManualClear(false);
    }
  }, [manualClear]);

  useEffect(() => {
    if (didInitSelection) return;
    const conns = agentData?.agentConnections || [];
    if (!conns.length) return;

    const preSelected = conns.filter((i) => i?.details).map((i) => i.id);
    setSelectedIds(preSelected);
    setDidInitSelection(true);
  }, [agentData?.agentConnections, didInitSelection, setSelectedIds]);

  const bulkSize = useMemo(
    () => Object.keys(bulkTaskMap || {}).length,
    [bulkTaskMap]
  );
  useEffect(() => {
    if (!bulkRunNonce) return;
    setBulkDoneCount(0);
  }, [bulkRunNonce]);

  useEffect(() => {
    const conns = agentData?.agentConnections || [];
    if (!conns.length) {
      setSelectedIds([]);
      return;
    }
    const preSelected = conns
      .filter((i) => i?.details?.detail?.checked ?? !!i?.details)
      .map((i) => i.id);

    setSelectedIds(preSelected);
  }, [agentData?.agentConnections, setSelectedIds]);

  return (
    <div className={styles.cont}>
      <div className={styles['header-cont']}>
        <div className={styles['search-cont']}>
          <SearchInput
            classname={styles.search}
            value={searchTerm}
            onChange={(name, value) => setSearchTerm(value)}
          />
        </div>
        <div className={`${styles['search-cont']} ${styles['buttons-cont']}`}>
          {rightButtons?.map((btn) => (
            <Button
              key={btn.label}
              label={btn.label}
              type={btn.svgType}
              // opicBackground={btn.opicBackground}
              onClick={btn.func}
              isLoading={btn.isSubmitting}
              {...btn}
            />
          ))}
        </div>
      </div>
      {isLoading || isFetching ? (
        <Loader isComponent />
      ) : (
        <APIRecords
          selectAllTrigger={selectAllTrigger}
          formik={formik}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          agentData={{ data: filteredData }}
          isPending={isPending}
          testStatuses={testStatuses}
          setTestStatuses={setTestStatuses}
          manualClear={manualClear}
          refetchAgentData={refetch}
          bulkTaskMap={bulkTaskMap}
          bulkRunNonce={bulkRunNonce}
          suppressRefetch
          onRowSyncComplete={() => {
            setBulkDoneCount((c) => {
              const next = c + 1;
              if (next === bulkSize && bulkSize > 0) refetch();
              return next;
            });
          }}
          setIsSyncingAll={setIsSyncingAll}
          isAgentConnected={isAgentConnected}
        />
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleDisconnect}
        closeOnSubmit={true}
        onApproveButton={{ text: 'Enable' }}
        text='Enable'
        subText='This will temporarily suspend their access to the system and all assigned tasks. You can enable their account again later if needed.'
        isUpdate
      />
    </div>
  );
};

export default APISection;
